<template>
	<CSettingsSection
		:loading="false"
		class="org-usage-table"
	>
		<h2 v-if="sectionTitle || loading" class="org-usage-table__heading sz-5 mb-3 flex items-center gap-3">
			<div v-if="loading" class="org-usage-table__title-skeleton skeleton"></div>
			<span v-else class="font-display">{{ sectionTitle }}</span>

			<template v-if="isNewBillingPipeline && showMonthPicker">
				<div v-if="loading" class="org-usage-table__dropdown-skeleton skeleton ml-auto"></div>
				<CDropdownMenu v-else-if="hasMultipleMonthOptions" v-model="currentMonthYear" :options="monthYearOptions" placement="bottom-start" class="ml-auto">
					<div class="inline-flex items-center gap-2 py-3">
						<Inline svg="date" class="text-icon-subtle" />
						<span class="font-normal text-2">{{ currentMonthYearLabel }}</span>
						<Inline svg="chevron-down" class="text-icon-subtle" />
					</div>
				</CDropdownMenu>
				<div v-else class="ml-auto py-3">
					<span class="font-normal text-2">{{ currentMonthYearLabel }}</span>
				</div>
			</template>
		</h2>

		<div
			v-if="loading"
			class="settings__loader-table settings__loader-table--odd border border-shade-3"
			:style="`--loader-row-height: ${loaderRowHeight}px`"
		>
			<div v-for="i in tableLoaderRows" :key="i" class="skeleton"></div>
		</div>

		<table v-else-if="hasVisibleUsageItems" class="table-style" :class="{ 'w-full': isNewBillingPipeline }">
			<thead>
				<tr>
					<th class="text-left">{{ isNewBillingPipeline ? 'Type' : '' }}</th>
					<th v-if="isNewBillingPipeline" class="text-left">Total Usage</th>
					<th class="text-left">Included</th>
					<th v-if="showAdditionalColumn" class="text-left">Additional</th>
					<th v-if="showCharges" class="text-right">Charges</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="item in visibleUsageItems" :key="item.Name">
					<template v-if="showItem(item)">
						<td>
							<div class="flex items-baseline gap-3">
								<Inline v-if="getIcon(item)" :svg="getIcon(item)" class="text-icon-subtle self-center" />
								{{ getDisplayName(item) }}
								<CToolTip v-if="item.ShowWarning && checkOverLimit(item) && getShowTooltip(item)" is-interactive show-on-click>
									<p v-if="item.Name === 'Entries'">New Entries are locked. <a :href="`/${orgCode}/settings/selectplan?source=orgsettings`">Upgrade</a> to get more entries.</p>
									<p v-else-if="item.Name === 'Storage'">File downloads are disabled. <a :href="`/${orgCode}/settings/selectplan?source=orgsettings`">Upgrade</a> to get more storage.</p>
									<p v-else-if="item.Name === 'Users'">
										You are on the Individual plan which only allows <strong>one</strong> user (you). The locked user(s) can no longer access your forms.
										<a :href="`/${orgCode}/settings/selectplan?source=orgsettings`">Upgrade</a> your plan to reenable access.
									</p>
									<template #button>
										<Inline svg="warning" class="text-icon-warning" />
									</template>
								</CToolTip>
								<Inline v-else-if="item.ShowWarning && checkOverLimit(item)" svg="warning" class="text-icon-warning self-end" />
							</div>
						</td>
						<td v-if="isNewBillingPipeline" title="Total Usage">
							{{ getTotalUsage(item) }} {{ item.Units || '' }}
						</td>
						<td title="Included">
							<template v-if="isNewBillingPipeline">
								{{ getIncluded(item) }} {{ item.Units || '' }}
							</template>
							<template v-else-if="showIncluded(item)">
								{{ getIncludedUsage(item) }}
								of
								{{ item.Limit ? formatNumber(item.Limit, item.Decimals) : 'Unlimited' }}
								{{ item.Units || '' }}
							</template>
						</td>
						<td v-if="showAdditionalColumn" title="Additional">
							<template v-if="isNewBillingPipeline">
								{{ getAnnualAdditionalUsage(item) }} {{ item.Units || '' }}
							</template>
							<template v-else-if="checkHasAdditionalUsage(item)">
								{{ getAdditionalUsage(item) }} {{ item.Units }}
								<span v-if="item.AdditionalUsagePricingDescription" class="sz-1 text-subtle">({{ item.AdditionalUsagePricingDescription }})</span>
							</template>
						</td>
						<td v-if="showCharges" class="text-right" title="Charges">
							{{ item.Charges }}
						</td>
					</template>
				</tr>
			</tbody>
		</table>

		<div v-else class="border border-shade-3 px-4 py-3 text-subtle">
			{{ emptyStateMessage }}
		</div>
	</CSettingsSection>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, onMounted, computed, ref, watch } from 'vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import type { UsageItemViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-item-view-model';
	import type { UsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-view-model';
	import CSettingsSection from './SettingsSection.vue';
	import CDropdownMenu from 'src/components/app/DropdownMenu.vue';
	import type { DropdownMenuItem } from 'src/components/app/DropdownMenuItem.vue';
	import session from '@cognitoforms/api/services/session';
	import { FeatureFlags } from '@cognitoforms/types/server-types/feature-flags';

	export default defineComponent({
		components: {
			CToolTip,
			CSettingsSection,
			CDropdownMenu
		},
		props: {
			usageData: { type: Object as PropType<UsageViewModel>, default: null },
			orgCode: { type: String, default: null },
			loading: { type: Boolean, default: true },
			showMonthPicker: { type: Boolean, default: true },
			showChargedLinesOnly: { type: Boolean, default: false },
			currMonth: { type: Number, default: null },
			currYear: { type: Number, default: null }
		},
		emits: ['component-loaded', 'month-year-change'],
		setup(props, { emit }) {
			onMounted(() => emit('component-loaded'));

			const locale = 'en-us';
			const monthsToShow = 18;
			const tableLoaderRows = 4;
			const loaderRowHeight = 36;
			const usageItems = computed(() => props.usageData?.Items ?? []);
			const isNewBillingPipeline = computed(() => {
				const enabledFeatures = session.value?.enabledFeatures as Record<string, boolean> | undefined;
				return (enabledFeatures?.AnnualPayment ?? false)
					|| (enabledFeatures?.[FeatureFlags.NewBillingPipeline] ?? false);
			});

			const getUsageValue = (item: UsageItemViewModel) => item.CurrentUsage ?? 0;
			const getLimitValue = (item: UsageItemViewModel) => item.Limit ?? null;
			const getDecimals = (item: UsageItemViewModel) => item.Decimals ?? 0;

			const icons: Record<string, string> = {
				'Users': 'users',
				'Entries': 'entries',
				'API Usage': 'api',
				'Document Templates': 'generated-documents',
				'Storage': 'data',
				'Guests': 'person'
			};

			const annualDisplayNames: Record<string, string> = {
				'API Usage': 'API Requests',
				'Document Templates': 'Documents'
			};

			const showItem = (item: UsageItemViewModel) => {
				if (item.Name !== 'Guests')
					return true;

				return (item.Limit ?? 0) > 0;
			};

			const showIncluded = (item: UsageItemViewModel) => {
				// Do not show included usage if the org is over their free tier of guests
				if (item.Name === 'Guests')
					return getUsageValue(item) <= (item.Limit ?? 0);

				return true;
			};

			const formatNumber = (number: number, decimals: number) => number.toLocaleString(locale, { maximumFractionDigits: decimals });

			const getAdditionalUsage = (item: UsageItemViewModel) => {
				let effectiveUsage: number;
				if (item.Name === 'Guests')
					effectiveUsage = getUsageValue(item);
				else
					effectiveUsage = getUsageValue(item) - (item.Limit ?? 0);

				return formatNumber(effectiveUsage, getDecimals(item));
			};

			const checkOverLimit = (item: UsageItemViewModel) => item.Limit != null && getUsageValue(item) > item.Limit;

			const checkHasAdditionalUsage = (item: UsageItemViewModel) => item.AdditionalUsageAllowed && checkOverLimit(item);

			const getIncludedUsage = (item: UsageItemViewModel) => {
				let effectiveUsage: number;
				if (item.AdditionalUsageAllowed && item.Limit)
					effectiveUsage = Math.min(getUsageValue(item), item.Limit);
				else
					effectiveUsage = getUsageValue(item);

				return formatNumber(effectiveUsage, getDecimals(item));
			};

			const getIncluded = (item: UsageItemViewModel) => item.Limit != null ? formatNumber(item.Limit, getDecimals(item)) : 'Unlimited';
			const getTotalUsage = (item: UsageItemViewModel) => formatNumber(getUsageValue(item), getDecimals(item));
			const getAnnualAdditionalUsageValue = (item: UsageItemViewModel) => {
				const limit = getLimitValue(item);
				if (limit == null)
					return 0;

				return Math.max(getUsageValue(item) - limit, 0);
			};
			const getAnnualAdditionalUsage = (item: UsageItemViewModel) => {
				return formatNumber(getAnnualAdditionalUsageValue(item), getDecimals(item));
			};

			const hasCharges = (item: UsageItemViewModel) => !!item.Charges && item.Charges.trim() !== '' && item.Charges !== '$0.00';

			const isChargedLine = (item: UsageItemViewModel) => {
				if (isNewBillingPipeline.value)
					return getAnnualAdditionalUsageValue(item) > 0 || hasCharges(item);

				return checkHasAdditionalUsage(item);
			};

			const getIcon = (item: UsageItemViewModel) => icons[item.Name];

			const getDisplayName = (item: UsageItemViewModel) => {
				if (!isNewBillingPipeline.value)
					return item.Name;

				return annualDisplayNames[item.Name] || item.Name;
			};

			const getShowTooltip = (item: UsageItemViewModel) => item.Name === 'Entries' || item.Name === 'Storage' || item.Name === 'Users';

			const toUtcMonthDate = (year: number, month: number) => new Date(Date.UTC(year, month - 1, 1));

			const toOptionValue = (date: Date) => `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`;
			const formatMonthYear = (date: Date) => date.toLocaleString('en-us', { month: 'long', year: 'numeric', timeZone: 'UTC' });

			const selectedDate = computed(() => {
				if (props.currMonth && props.currYear)
					return toUtcMonthDate(props.currYear, props.currMonth);

				if (props.usageData?.Month)
					return new Date(props.usageData.Month);

				const now = new Date();
				return toUtcMonthDate(now.getUTCFullYear(), now.getUTCMonth() + 1);
			});

			const now = new Date();
			const latestDropdownMonth = ref(toUtcMonthDate(now.getUTCFullYear(), now.getUTCMonth() + 1));
			const anchorToMonth = (month: string | undefined) => {
				if (!month)
					return false;

				const date = new Date(month);
				latestDropdownMonth.value = toUtcMonthDate(date.getUTCFullYear(), date.getUTCMonth() + 1);
				return true;
			};
			if (!anchorToMonth(props.usageData?.Month)) {
				const stopAnchorWatcher = watch(() => props.usageData?.Month, month => {
					if (anchorToMonth(month))
						stopAnchorWatcher();
				});
			}
			const currentMonthYear = ref(toOptionValue(selectedDate.value));

			watch(selectedDate, value => {
				currentMonthYear.value = toOptionValue(value);
			});

			const monthYearOptions = computed(() => {
				const oldestMemberDateCreated = props.usageData?.OldestMemberDateCreated;
				const earliestAllowedMonth = (() => {
					if (!oldestMemberDateCreated)
						return null;

					const createdDate = new Date(oldestMemberDateCreated);
					return new Date(Date.UTC(createdDate.getUTCFullYear(), createdDate.getUTCMonth(), 1));
				})();

				const options: DropdownMenuItem[] = [];
				for (let i = 0; i < monthsToShow; i++) {
					const date = new Date(Date.UTC(latestDropdownMonth.value.getUTCFullYear(), latestDropdownMonth.value.getUTCMonth() - i, 1));
					if (earliestAllowedMonth && date < earliestAllowedMonth)
						break;

					const displayValue = formatMonthYear(date);
					options.push({
						value: toOptionValue(date),
						displayValue
					});
				}

				return options;
			});

			const currentMonthYearLabel = computed(() => {
				const matchedOption = monthYearOptions.value.find(option => option.value === currentMonthYear.value);
				return matchedOption?.displayValue || formatMonthYear(selectedDate.value);
			});
			const hasMultipleMonthOptions = computed(() => monthYearOptions.value.length > 1);

			watch(currentMonthYear, value => {
				const [yearPart, monthPart] = value.split('-');
				const year = parseInt(yearPart, 10);
				const month = parseInt(monthPart, 10);

				if (!Number.isFinite(year) || !Number.isFinite(month))
					return;

				if (value === toOptionValue(selectedDate.value))
					return;

				emit('month-year-change', { year, month });
			});

			const showCharges = computed(() => {
				const isCurrentMonth = toOptionValue(selectedDate.value) === toOptionValue(latestDropdownMonth.value);
				return isNewBillingPipeline.value && (props.showChargedLinesOnly || !isCurrentMonth);
			});

			const visibleUsageItems = computed(() => usageItems.value
				.filter(i => showItem(i))
				.filter(i => !props.showChargedLinesOnly || isChargedLine(i)));
			const hasVisibleUsageItems = computed(() => visibleUsageItems.value.length > 0);
			const sectionTitle = computed(() => {
				return props.usageData.Month ? `${new Date(props.usageData.Month).toLocaleString('en-us', { month: 'long', timeZone: 'UTC' })} Usage` : '';
			});
			const emptyStateMessage = computed(() => {
				if (isNewBillingPipeline.value)
					return 'No historic usage is available for the selected month.';

				return 'No usage data is available for this month.';
			});

			return {
				showAdditionalColumn: computed(() => isNewBillingPipeline.value || usageItems.value.some((i: UsageItemViewModel) => checkHasAdditionalUsage(i))),
				locale, // Usage is not currently localized to the org,
				loaderRowHeight,
				tableLoaderRows,
				formatNumber,
				checkOverLimit,
				checkHasAdditionalUsage,
				getIncludedUsage,
				getTotalUsage,
				getIncluded,
				getAnnualAdditionalUsage,
				getIcon,
				getDisplayName,
				getShowTooltip,
				showIncluded,
				getAdditionalUsage,
				showItem,
				currentMonthYear,
				currentMonthYearLabel,
				hasMultipleMonthOptions,
				monthYearOptions,
				isNewBillingPipeline,
				showCharges,
				visibleUsageItems,
				hasVisibleUsageItems,
				emptyStateMessage,
				sectionTitle
			};
		}
	});
</script>

<style lang="scss">
.org-usage-table {

	&__heading {
		min-height: 36px;
	}

	&__title-skeleton {
		width: 170px;
		height: 32px;
	}

	&__dropdown-skeleton {
		width: 140px;
		height: 28px;
	}

	.settings__loader {
		max-width: 300px;
	}

	table {

		.text-icon-subtle {
			@include b-down($b4) {
				display: none;
			}
		}
	}

	@include b-down($b3) {

		.settings__loader {
			max-width: none;
		}

		table {
			width: 100%;
		}
	}
}
</style>